import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatusBadge, SearchBar, Pagination, EmptyState, useList, formatDate } from "@/components/shared";
import RightDrawer from "@/components/RightDrawer";
import { Radio, Phone, Users, FileText, MessagesSquare, Send } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Channels() {
  const { data, pagination, loading, page, setPage, q, setQ, filters, setFilters } = useList("/channels");
  const [selected, setSelected] = useState(null);
  const [summary, setSummary] = useState(null);

  const openDetail = async (ch) => {
    setSelected(ch);
    setSummary(null);
    const res = await api.get(`/channels/${ch.id}/summary`);
    setSummary(res.data);
  };

  return (
    <div>
      <PageHeader title="Channel" subtitle="Nomor WhatsApp Business yang telah diprovisioning untuk Anda." />

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <SearchBar value={q} onChange={setQ} placeholder="Cari channel..." testid="channel-search" />
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-44 bg-white" data-testid="channel-status-filter"><SelectValue placeholder="Semua status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua status</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="inactive">Nonaktif</SelectItem>
            <SelectItem value="setup_required">Perlu Setup</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {!loading && data.length === 0 ? (
        <EmptyState icon={Radio} title="Belum ada channel" description="Channel WhatsApp Business Anda akan muncul di sini setelah diprovisioning oleh tim Serabi." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((ch) => (
            <button key={ch.id} onClick={() => openDetail(ch)} data-testid={`channel-card-${ch.id}`}
              className="text-left bg-white p-5 rounded-lg hover:bg-[#FCFCFC] transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#003399]/10 flex items-center justify-center">
                  <Radio className="w-5 h-5 text-[#003399]" />
                </div>
                <StatusBadge status={ch.status} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-neutral-900 mb-1">{ch.name}</h3>
              <p className="text-sm text-neutral-500 flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5" /> {ch.display_phone_number}
              </p>
            </button>
          ))}
        </div>
      )}

      <Pagination pagination={pagination} onPage={setPage} />

      <RightDrawer open={!!selected} onClose={() => setSelected(null)} title={selected?.name} description="Detail channel" testid="channel-drawer">
        {selected && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <StatusBadge status={selected.status} />
              <span className="text-sm text-neutral-500">Dibuat {formatDate(selected.created_at, false)}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nama Channel" value={selected.name} />
              <Field label="Nomor Bisnis" value={selected.display_phone_number} />
            </div>
            <div>
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-neutral-500 mb-3">Ringkasan Channel</p>
              <div className="grid grid-cols-2 gap-3">
                <SummaryTile icon={Users} label="Kontak" value={summary?.contacts} />
                <SummaryTile icon={MessagesSquare} label="Percakapan" value={summary?.conversations} />
                <SummaryTile icon={FileText} label="Template" value={summary?.templates} />
                <SummaryTile icon={Send} label="Pesan" value={summary?.messages} />
              </div>
            </div>
          </div>
        )}
      </RightDrawer>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-neutral-500 mb-1">{label}</p>
    <p className="text-sm font-medium text-neutral-900">{value || "-"}</p>
  </div>
);

const SummaryTile = ({ icon: Icon, label, value }) => (
  <div className="bg-[#F5F5F5] rounded-[4px] p-4 flex items-center gap-3">
    <Icon className="w-5 h-5 text-[#003399]" />
    <div>
      <p className="font-heading text-xl font-bold text-neutral-900">{value ?? "-"}</p>
      <p className="text-xs text-neutral-500">{label}</p>
    </div>
  </div>
);
